/**
 * useJourneyHistory — REQ-090 state machine for the admin Journey History &
 * Stop Points module (ODE-P07-T06).
 *
 * The module consumes the VERIFIED ODE-P04-T09 server read model unchanged:
 *
 *   GET /v1/admin/journey-history            (bounded page + filters)
 *   GET /v1/admin/journey-history/:journeyId (route polyline + stops + distance)
 *
 * List flow: `pending` (filters not yet submitted) → `submit()` → `loading` →
 * the guarded read outcome:
 *   200 → `ready` (server page; an empty `journeys` array is the REQ-090 empty
 *         state rendered by the module, never a fabricated row)
 *   400 → `error`   (invalid filter — the server rejected it, retryable with
 *         corrected input)
 *   401 → `unauthorized`
 *   403 → `blocked` (server-enforced; the client mirror is UX only)
 *   503/network → `error`
 *   malformed 200 payload → `error` (never rendered as an empty/valid page).
 *         The payload guards validate the NESTED elements too (each summary
 *         row, each polyline point, each stop point, each gap): a 200 with
 *         `journeys: [null]` or `polyline: [null]` fails closed into the
 *         error state instead of throwing during render (review R-B1/R-B2).
 *
 * Detail flow (`openDetail(journeyId)`) is a separate read-only projection
 * with its own state (`idle`/`loading`/`ready`/`not_found`/`unauthorized`/
 * `blocked`/`error`). Nothing here derives journey data: the polyline order
 * (REQ-040), the stop points (REQ-041) and the cumulative distance (REQ-042)
 * are rendered from the server projection verbatim.
 *
 * Pagination is server-driven and bounded: `nextPage()`/`prevPage()` re-query
 * the server with the next/previous 1-based page; the client never slices or
 * re-pages a payload locally. Filters that cannot be encoded for the server
 * (malformed journey id, out-of-bounds limit, inverted/oversized date range)
 * are reported as an explicit validation message and are NOT sent — the
 * client never silently drops a filter.
 *
 * Traces to: ODE-PRD-REQ-040, ODE-PRD-REQ-041, ODE-PRD-REQ-042,
 *            ODE-PRD-REQ-046, ODE-PRD-REQ-090
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type JourneyHistoryDetail,
  type JourneyHistoryDetailFetchResult,
  type JourneyHistoryGap,
  type JourneyHistoryListFetchResult,
  type JourneyHistoryListQuery,
  type JourneyHistoryListResult,
  type JourneyHistoryPolylinePoint,
  type JourneyHistoryStopPoint,
  type JourneyHistorySummary,
} from '../../api/client.js';
import {
  JOURNEY_LIST_DEFAULT_PAGE_SIZE,
  JOURNEY_LIST_MAX_PAGE_SIZE,
  JOURNEY_MAX_DATE_RANGE_DAYS,
} from './derivation-config.js';

/** Journey id wire contract: canonical tracking-session UUID (REQ-040 key). */
export const JOURNEY_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Driver id wire contract (opaque id, never an email/PII). */
export const DRIVER_ID_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The raw filter form (all values are strings; empties are omitted). */
export interface JourneyHistoryForm {
  readonly driverId: string;
  readonly journeyId: string;
  /** Inclusive start date of the journey-start window (`YYYY-MM-DD`, UTC). */
  readonly from: string;
  /** Inclusive end date of the journey-start window (`YYYY-MM-DD`, UTC). */
  readonly to: string;
  /** Bounded page size (1..JOURNEY_LIST_MAX_PAGE_SIZE); empty = server default. */
  readonly limit: string;
}

export const EMPTY_JOURNEY_HISTORY_FORM: JourneyHistoryForm = {
  driverId: '',
  journeyId: '',
  from: '',
  to: '',
  limit: '',
};

export type JourneyHistoryListState =
  | { readonly kind: 'pending' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly result: JourneyHistoryListResult; readonly loadingPage: boolean }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export type JourneyHistoryDetailState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly journeyId: string }
  | { readonly kind: 'ready'; readonly detail: JourneyHistoryDetail }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export interface UseJourneyHistoryOptions {
  readonly token: string | null;
  /** Test seam: journey list fetch (default: the shared api client). */
  readonly list?:
    | ((query: JourneyHistoryListQuery, token: string) => Promise<JourneyHistoryListFetchResult>)
    | undefined;
  /** Test seam: journey detail fetch (default: the shared api client). */
  readonly detail?:
    | ((journeyId: string, token: string) => Promise<JourneyHistoryDetailFetchResult>)
    | undefined;
}

export interface UseJourneyHistoryResult {
  readonly form: JourneyHistoryForm;
  readonly setForm: (patch: Partial<JourneyHistoryForm>) => void;
  /** Fail-closed filter validation message, or null when the form is encodable. */
  readonly validationError: string | null;
  readonly submit: () => void;
  readonly reset: () => void;
  readonly nextPage: () => void;
  readonly prevPage: () => void;
  readonly state: JourneyHistoryListState;
  readonly detailState: JourneyHistoryDetailState;
  readonly openDetail: (journeyId: string) => void;
  readonly closeDetail: () => void;
}

/**
 * Converts a `YYYY-MM-DD` filter date to an inclusive epoch-milliseconds
 * bound (UTC): `start` → 00:00:00.000Z, `end` → 23:59:59.999Z. This is
 * query-parameter ENCODING for the P04-T09 `from`/`to` contract — it is not
 * journey-data derivation (the server owns every derived value). Returns
 * null for a malformed calendar date.
 */
export function dateToEpochMs(date: string, edge: 'start' | 'end'): number | null {
  const match = DATE_PATTERN.exec(date);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utc = Date.UTC(year, month - 1, day);
  const probe = new Date(utc);
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null;
  }
  return edge === 'start' ? utc : utc + DAY_MS - 1;
}

/**
 * Fail-closed form validation (mirror of the server query bounds). Returns a
 * human-readable message, or null when every filter can be encoded for the
 * P04-T09 list query.
 */
export function validateJourneyHistoryForm(form: JourneyHistoryForm): string | null {
  if (form.driverId.trim() !== '' && !DRIVER_ID_PATTERN.test(form.driverId.trim())) {
    return 'Driver harus cocok dengan ^[A-Za-z0-9._-]{1,64}$.';
  }
  if (form.journeyId.trim() !== '' && !JOURNEY_ID_PATTERN.test(form.journeyId.trim())) {
    return 'Journey ID harus berupa UUID.';
  }
  const limit = form.limit.trim();
  if (limit !== '') {
    const parsed = Number(limit);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > JOURNEY_LIST_MAX_PAGE_SIZE) {
      return `Batas baris harus bilangan bulat 1..${JOURNEY_LIST_MAX_PAGE_SIZE}.`;
    }
  }
  const fromRaw = form.from.trim();
  const toRaw = form.to.trim();
  const from = fromRaw === '' ? null : dateToEpochMs(fromRaw, 'start');
  const to = toRaw === '' ? null : dateToEpochMs(toRaw, 'end');
  if (fromRaw !== '' && from === null) return 'Tanggal awal tidak valid (YYYY-MM-DD).';
  if (toRaw !== '' && to === null) return 'Tanggal akhir tidak valid (YYYY-MM-DD).';
  if (from !== null && to !== null) {
    if (from > to) return 'Tanggal awal tidak boleh setelah tanggal akhir.';
    if (to - from > JOURNEY_MAX_DATE_RANGE_DAYS * DAY_MS) {
      return `Rentang tanggal maksimum ${JOURNEY_MAX_DATE_RANGE_DAYS} hari.`;
    }
  }
  return null;
}

/**
 * Encodes the form into the P04-T09 list query (empties omitted, dates as
 * inclusive epoch-ms bounds, limit defaulted server-side). Returns null when
 * the form is not encodable (see [validateJourneyHistoryForm]).
 */
export function toJourneyHistoryQuery(
  form: JourneyHistoryForm,
  page: number,
): JourneyHistoryListQuery | null {
  if (validateJourneyHistoryForm(form) !== null) return null;
  const query: {
    page: number;
    limit: number;
    driverId?: string;
    journeyId?: string;
    from?: number;
    to?: number;
  } = {
    page,
    limit: form.limit.trim() === '' ? JOURNEY_LIST_DEFAULT_PAGE_SIZE : Number(form.limit.trim()),
  };
  if (form.driverId.trim() !== '') query.driverId = form.driverId.trim();
  if (form.journeyId.trim() !== '') query.journeyId = form.journeyId.trim();
  const from = form.from.trim() === '' ? null : dateToEpochMs(form.from.trim(), 'start');
  const to = form.to.trim() === '' ? null : dateToEpochMs(form.to.trim(), 'end');
  if (from !== null) query.from = from;
  if (to !== null) query.to = to;
  return query;
}

/** True when the value is a finite number (never NaN/Infinity). */
function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** True when the value is a finite number or an explicit null. */
function isFiniteNumberOrNull(value: unknown): value is number | null {
  return value === null || isFiniteNumber(value);
}

/** True when the value is an object with the given keys (not null/array). */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * True when the value is a well-formed journey summary row (REQ-046 list
 * surface). Every declared wire field is validated, so a null/malformed row
 * fails closed into the REQ-090 error state instead of crashing the render.
 */
export function isJourneyHistorySummary(value: unknown): value is JourneyHistorySummary {
  if (!isRecord(value)) return false;
  return (
    typeof value.journeyId === 'string' &&
    typeof value.deliveryOrderId === 'string' &&
    typeof value.driverId === 'string' &&
    typeof value.state === 'string' &&
    isFiniteNumberOrNull(value.startedAtEpochMs) &&
    isFiniteNumberOrNull(value.endedAtEpochMs) &&
    isFiniteNumber(value.sampleCount) &&
    isFiniteNumberOrNull(value.firstFixAtEpochMs) &&
    isFiniteNumberOrNull(value.lastFixAtEpochMs) &&
    isFiniteNumber(value.distanceMeters) &&
    isFiniteNumber(value.stopPointCount)
  );
}

/** True when the value is a well-formed route polyline point (REQ-040). */
export function isJourneyHistoryPolylinePoint(
  value: unknown,
): value is JourneyHistoryPolylinePoint {
  if (!isRecord(value)) return false;
  return (
    isFiniteNumber(value.sequence) &&
    isFiniteNumber(value.capturedAtEpochMs) &&
    isFiniteNumber(value.latitude) &&
    isFiniteNumber(value.longitude) &&
    isFiniteNumberOrNull(value.accuracyMeters)
  );
}

/** True when the value is a well-formed derived stop point (REQ-041). */
export function isJourneyHistoryStopPoint(value: unknown): value is JourneyHistoryStopPoint {
  if (!isRecord(value)) return false;
  return (
    typeof value.stopId === 'string' &&
    isFiniteNumber(value.latitude) &&
    isFiniteNumber(value.longitude) &&
    isFiniteNumber(value.arrivedAtEpochMs) &&
    isFiniteNumber(value.departedAtEpochMs) &&
    isFiniteNumber(value.dwellMs) &&
    isFiniteNumber(value.sampleCount)
  );
}

/** True when the value is a well-formed recorded tracking gap. */
export function isJourneyHistoryGap(value: unknown): value is JourneyHistoryGap {
  if (!isRecord(value)) return false;
  return (
    isFiniteNumber(value.fromEpochMs) &&
    isFiniteNumberOrNull(value.toEpochMs) &&
    typeof value.reason === 'string'
  );
}

/**
 * True when the payload is a well-formed journey list result. Each summary
 * element is validated (nested guard) so a `journeys: [null]` 200 payload
 * fails closed instead of throwing during render (review finding R-B1).
 */
export function isJourneyHistoryListResult(value: unknown): value is JourneyHistoryListResult {
  if (!isRecord(value)) return false;
  return (
    Number.isInteger(value.page) &&
    Number.isInteger(value.limit) &&
    Number.isInteger(value.total) &&
    typeof value.hasMore === 'boolean' &&
    Array.isArray(value.journeys) &&
    value.journeys.every(isJourneyHistorySummary)
  );
}

/**
 * True when the payload is a well-formed journey detail projection. The
 * summary fields plus every nested polyline/stop/gap element are validated,
 * so malformed nested entries (`polyline: [null]`, bad stop/gap elements)
 * fail closed instead of throwing during render (review finding R-B2).
 */
export function isJourneyHistoryDetail(value: unknown): value is JourneyHistoryDetail {
  if (!isJourneyHistorySummary(value)) return false;
  const candidate = value as unknown as Record<string, unknown>;
  return (
    Array.isArray(candidate.polyline) &&
    candidate.polyline.every(isJourneyHistoryPolylinePoint) &&
    Array.isArray(candidate.stopPoints) &&
    candidate.stopPoints.every(isJourneyHistoryStopPoint) &&
    Array.isArray(candidate.gaps) &&
    candidate.gaps.every(isJourneyHistoryGap)
  );
}

export function useJourneyHistory(options: UseJourneyHistoryOptions): UseJourneyHistoryResult {
  const { token } = options;
  const [form, setFormState] = useState<JourneyHistoryForm>(EMPTY_JOURNEY_HISTORY_FORM);
  const [state, setState] = useState<JourneyHistoryListState>({ kind: 'pending' });
  const [detailState, setDetailState] = useState<JourneyHistoryDetailState>({ kind: 'idle' });
  const [request, setRequest] = useState<
    { readonly attempt: number; readonly page: number; readonly query: JourneyHistoryListQuery } | null
  >(null);
  const attemptRef = useRef(0);
  const requestRef = useRef<typeof request>(null);
  requestRef.current = request;
  const stateRef = useRef<JourneyHistoryListState>(state);
  stateRef.current = state;

  const listRef = useRef(
    options.list ??
      ((query: JourneyHistoryListQuery, tokenValue: string) =>
        api.listJourneyHistory(query, tokenValue)),
  );
  listRef.current =
    options.list ??
    ((query: JourneyHistoryListQuery, tokenValue: string) =>
      api.listJourneyHistory(query, tokenValue));
  const detailRef = useRef(
    options.detail ??
      ((journeyId: string, tokenValue: string) =>
        api.fetchJourneyHistoryDetail(journeyId, tokenValue)),
  );
  detailRef.current =
    options.detail ??
    ((journeyId: string, tokenValue: string) =>
      api.fetchJourneyHistoryDetail(journeyId, tokenValue));

  const validationError = validateJourneyHistoryForm(form);

  const setForm = useCallback((patch: Partial<JourneyHistoryForm>) => {
    setFormState((current) => ({ ...current, ...patch }));
  }, []);

  /** Submits the current filters as a fresh page-1 read (fail-closed). */
  const submit = useCallback(() => {
    const query = toJourneyHistoryQuery(form, 1);
    if (query === null) return;
    attemptRef.current += 1;
    setRequest({ attempt: attemptRef.current, page: 1, query });
  }, [form]);

  const reset = useCallback(() => {
    setFormState(EMPTY_JOURNEY_HISTORY_FORM);
    setRequest(null);
    setState({ kind: 'pending' });
    setDetailState({ kind: 'idle' });
  }, []);

  // Server-driven page read: a fresh submit replaces the page; next/prev
  // re-query the server with the adjacent 1-based page (never client-sliced).
  useEffect(() => {
    if (request === null) return undefined;
    if (token === null) {
      setState({ kind: 'unauthorized' });
      return undefined;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    // The page lives on the request (filters are page-independent): the read
    // always re-queries the server for the requested 1-based page.
    listRef
      .current({ ...request.query, page: request.page }, token)
      .then((result) => {
        if (cancelled) return;
        setState(mapListResult(result));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({ kind: 'error', message: error instanceof Error ? error.message : 'Network error' });
      });
    return () => {
      cancelled = true;
    };
  }, [token, request]);

  const nextPage = useCallback(() => {
    const current = requestRef.current;
    const latest = stateRef.current;
    if (current === null || latest.kind !== 'ready' || !latest.result.hasMore) return;
    attemptRef.current += 1;
    setRequest({ attempt: attemptRef.current, page: current.page + 1, query: current.query });
  }, []);

  const prevPage = useCallback(() => {
    const current = requestRef.current;
    if (current === null || current.page <= 1) return;
    attemptRef.current += 1;
    setRequest({ attempt: attemptRef.current, page: current.page - 1, query: current.query });
  }, []);

  const openDetail = useCallback(
    (journeyId: string) => {
      if (token === null) {
        setDetailState({ kind: 'unauthorized' });
        return;
      }
      setDetailState({ kind: 'loading', journeyId });
      void detailRef
        .current(journeyId, token)
        .then((result) => {
          setDetailState(mapDetailResult(result));
        })
        .catch((error: unknown) => {
          setDetailState({
            kind: 'error',
            message: error instanceof Error ? error.message : 'Network error',
          });
        });
    },
    [token],
  );

  const closeDetail = useCallback(() => {
    setDetailState({ kind: 'idle' });
  }, []);

  return {
    form,
    setForm,
    validationError,
    submit,
    reset,
    nextPage,
    prevPage,
    state,
    detailState,
    openDetail,
    closeDetail,
  };
}

function mapListResult(result: JourneyHistoryListFetchResult): JourneyHistoryListState {
  switch (result.status) {
    case 200:
      return isJourneyHistoryListResult(result.payload)
        ? { kind: 'ready', result: result.payload, loadingPage: false }
        : { kind: 'error', message: 'Respons daftar perjalanan tidak valid.' };
    case 401:
      return { kind: 'unauthorized' };
    case 403:
      return { kind: 'blocked', message: result.message ?? 'Forbidden' };
    default:
      return { kind: 'error', message: result.message ?? `HTTP ${result.status}` };
  }
}

function mapDetailResult(result: JourneyHistoryDetailFetchResult): JourneyHistoryDetailState {
  switch (result.status) {
    case 200:
      return isJourneyHistoryDetail(result.payload)
        ? { kind: 'ready', detail: result.payload }
        : { kind: 'error', message: 'Respons detail perjalanan tidak valid.' };
    case 404:
      return { kind: 'not_found' };
    case 401:
      return { kind: 'unauthorized' };
    case 403:
      return { kind: 'blocked', message: result.message ?? 'Forbidden' };
    default:
      return { kind: 'error', message: result.message ?? `HTTP ${result.status}` };
  }
}
